import React, {useEffect, useState} from "react";
import {Input, DatePicker, Modal, Button, Space, Checkbox, AutoComplete, Select} from "antd";
import { CopyOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import "./OGLAssignment.css";
import moment from "moment";
import {House} from "../../../../types/House";
import {getConfig} from "../../../../config";
import {useProjectContext} from "../../../../context/ProjectContext";
import axios from "axios";
import dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/de_DE";

const OGLAssignment: React.FC<{
    houses: House[];
    setHouses: React.Dispatch<React.SetStateAction<House[]>>;
    selectedHouseId: number;
    selectedProject: string | undefined;
    setSelectedHouseId: React.Dispatch<React.SetStateAction<number>>;
}> = ({ houses, setHouses, selectedHouseId, setSelectedHouseId, selectedProject }) => {
    const selectedHouse = houses.find((h) => h.id === selectedHouseId) || houses[0];
    const [sections, setSections] = useState<{ [key: string]: { name: string }[] }>({
        Innen: [],
        Aussen: [],
        Umgebung: [],
    });

    const { API_BASE_URL } = getConfig();
    const [dates, setDates] = useState<{ [key: string]: string | null }>({
        Bezug: null,
        Geruest: null,
        VormontageAussen: null,
        VormontageInnen: null,
    });

    const [modalVisible, setModalVisible] = useState(false);
    const [copyTarget, setCopyTarget] = useState<{ section: string; name: string } | null>(null);
    const [copyAmount, setCopyAmount] = useState(1);
    const [increment, setIncrement] = useState(true)
    const [supplements, setSupplements] = useState<string[]>([]);

    useEffect(() => {
        const fetchSupplements = async () => {
            try {
                const response = await axios.get<string[]>(
                    `${API_BASE_URL}/getAllSupplementsOfProject?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${selectedProject}`
                );
                setSupplements(response.data);
                if (response.data.length > 0) {
                    updateHouseField("supplement", response.data[0]);
                }
            } catch (error) {
                console.error("Error fetching supplements:", error);
            }
        };
        fetchSupplements();
    }, [selectedProject]);

    
    const handleDateChange = (section: string, date: any, dateString: string | string[]) => {
        setDates((prev) => ({
            ...prev,
            [section]: Array.isArray(dateString) ? dateString[0] : dateString || null,
        }));
    };

    const addComponent = (section: string) => {
        setSections((prev) => ({
            ...prev,
            [section]: [...prev[section], { name: `OG ${prev[section].length + 1}` }],
        }));
    };

    const deleteComponent = (section: string, index: number) => {
        setSections((prev) => ({
            ...prev,
            [section]: prev[section].filter((_, i) => i !== index),
        }));
    };

    const copyComponent = (section: string, index: number) => {
        setSections((prev) => ({
            ...prev,
            [section]: [
                ...prev[section].slice(0, index + 1),
                { ...prev[section][index] },
                ...prev[section].slice(index + 1),
            ],
        }));
    };

    const openCopyManyModal = (section: string, name: string) => {
        setCopyTarget({ section, name });
        setModalVisible(true);
    };

    const handleCopyMany = () => {
        if (!copyTarget) return;
        setHouses((prev) =>
            prev.map((house) => {
                if (house.id !== selectedHouse.id) return house;
                const baseName = copyTarget.name;
                const newCopies = Array.from({ length: copyAmount }, (_, i) => ({
                    name: increment ? incrementName(baseName, i + 1) : baseName,
                }));
                return {
                    ...house,
                    sections: {
                        ...house.sections,
                        [copyTarget.section]: [
                            ...house.sections[copyTarget.section as keyof typeof house.sections],
                            ...newCopies,
                        ],
                    },
                };
            })
        );
        setModalVisible(false);
    };

    const dateLabelMap: Record<string, string> = {
        Bezug: "Bezug",
        Geruest: "Gerüst",
        VormontageAussen: "Vormontage (A)",
        VormontageInnen: "Vormontage (I)"
    }

    const handleInputChange = (section: string, index: number, newValue: string) => {
        setSections((prev) => {
            const updatedSection = [...prev[section]];
            updatedSection[index].name = newValue;
            return { ...prev, [section]: updatedSection };
        });
    };

    const incrementName = (name: string, step: number): string => {
        const match = name.match(/^(\d+)([.\s])?\s(.*)$|^(.*?)(\d+|\s[A-Za-z])$/);
        if (!match) return name; // No match, return original name
        if (match[1] !== undefined) {
            let num = parseInt(match[1], 10) + step;
            return `${num}${match[2] ? match[2] : ""} ${match[3]}`;
        } else {
            const base = match[4]; // Static text before the number/letter
            let suffix = match[5].trim(); // Number or letter to increment
            if (!isNaN(Number(suffix))) {
                let num = parseInt(suffix, 10) + step;
                return `${base}${num}`;
            } else if (suffix.length === 1 && /^[A-Za-z]$/.test(suffix)) {
                let charCode = suffix.charCodeAt(0) + step;
                return `${base} ${String.fromCharCode(charCode)}`;
            }
        }
        return name;
    };

    const addNewHouse = () => {
        const newHouse = {
            id: houses.length + 1,
            name: `Haus ${houses.length + 1}`,
            supplement: "0",
            apartments: 0, // Neu: Wohnungen setzen
            type: "Wohngebäude", // Neu: Standardtyp setzen
            sections: { Innen: [], Aussen: [], Umgebung: [] },
            dates: { Bezug: null, Geruest: null, VormontageAussen: null, VormontageInnen: null },
        };
        setHouses([...houses, newHouse]);
    };


    const updateHouseField = (key: keyof typeof selectedHouse, value: any) => {
        setHouses((prev) =>
            prev.map((house) => (house.id === selectedHouse.id ? { ...house, [key]: value } : house))
        );
    };

    const updateHouseDate = (dateKey: keyof typeof selectedHouse.dates, dateString: string | null) => {
        setHouses((prev) =>
            prev.map((house) =>
                house.id === selectedHouse.id
                    ? {
                        ...house,
                        dates: {
                            ...house.dates,
                            [dateKey]: dateString || null, // Ensure the correct field is updated
                        },
                    }
                    : house
            )
        );
    };

    const updateSectionDate = (section: keyof typeof selectedHouse.sections, dateString: string | null) => {
        setHouses((prev) =>
            prev.map((house) =>
                house.id === selectedHouse.id
                    ? {
                        ...house,
                        sections: {
                            ...house.sections,
                            [section]: (house.sections[section] as { name: string; date?: string | null }[]).map(
                                (s) => ({
                                    ...s,
                                    date: dateString || null, // Store Messbereit date inside section
                                })
                            ),
                        },
                    }
                    : house
            )
        );
    };

    const addSectionComponent = (section: keyof typeof selectedHouse.sections) => {
        setHouses((prev) =>
            prev.map((house) =>
                house.id === selectedHouse.id
                    ? {
                        ...house,
                        sections: {
                            ...house.sections,
                            [section]: [
                                ...house.sections[section],
                                { name: `OG ${house.sections[section].length + 1}` },
                            ],
                        },
                    }
                    : house
            )
        );
    };

    const updateSectionComponent = (section: keyof typeof selectedHouse.sections, index: number, newValue: string) => {
        setHouses((prev) =>
            prev.map((house) =>
                house.id === selectedHouse.id
                    ? {
                        ...house,
                        sections: {
                            ...house.sections,
                            [section]: house.sections[section].map((s, i) =>
                                i === index ? { ...(s as { name: string }), name: newValue } : s
                            ),
                        },
                    }
                    : house
            )
        );
    };

    const deleteSectionComponent = (section: keyof typeof selectedHouse.sections, index: number) => {
        setHouses((prev) =>
            prev.map((house) =>
                house.id === selectedHouse.id
                    ? {
                        ...house,
                        sections: {
                            ...house.sections,
                            [section]: house.sections[section].filter((_, i) => i !== index),
                        },
                    }
                    : house
            )
        );
    };

    const copySectionComponent = (section: keyof typeof selectedHouse.sections, index: number) => {
        setHouses((prev) =>
            prev.map((house) =>
                house.id === selectedHouse.id
                    ? {
                        ...house,
                        sections: {
                            ...house.sections,
                            [section]: [
                                ...house.sections[section].slice(0, index + 1),
                                { ...(house.sections[section] as { name: string }[])[index] },
                                ...house.sections[section].slice(index + 1),
                            ],
                        },
                    }
                    : house
            )
        );
    };
    return (
        <div className="step-two-container">
            <div className="navigation-container">
                <Button type="primary" icon={<PlusOutlined/>} onClick={addNewHouse} size="small"
                        className="add-house-btn"/>
                <div className="navigation-bar">
                    {houses.map((house) => (
                        <div
                            key={house.id}
                            className={`nav-item ${selectedHouseId === house.id ? "active" : ""}`}
                            onClick={() => setSelectedHouseId(house.id)}
                        >
                            <strong>{house.name}</strong>
                            <div className="nav-dates">B: {house.dates.Bezug || "-"}</div>
                            <div className="nav-dates">G: {house.dates.Geruest || "-"}</div>
                            <div className="nav-dates">V: {house.dates.VormontageAussen || "-"}</div>
                            <div className="nav-dates">V: {house.dates.VormontageInnen || "-"}</div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="house-section">
                <div className="house-controls">
                    <Button
                        icon={<CopyOutlined/>}
                        size="small"
                        onClick={() => {
                            const newHouse = {
                                ...selectedHouse,
                                id: houses.length + 1,
                                name: `${selectedHouse.name} (Kopie)`,
                            };
                            setHouses((prev) => [...prev, newHouse]);
                        }}
                    />
                    <Button
                        icon={<DeleteOutlined/>}
                        size="small"
                        danger
                        onClick={() => {
                            if (houses.length > 1) {
                                setHouses((prev) => prev.filter((house) => house.id !== selectedHouse.id));
                                setSelectedHouseId(houses[0].id);
                            }
                        }}
                    />
                </div>
                <div style={{display: "flex", flexDirection: "column", gap: "2px"}}>
                    <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
                        {/* Bauabschnitt */}
                        <div style={{display: "flex", flexDirection: "column", alignItems: "center"}}>
                            <span style={{
                                fontSize: "12px",
                                fontWeight: "500",
                                lineHeight: "1",
                                marginBottom: "2px"
                            }}>Bauabschnitt</span>
                            <Input
                                value={selectedHouse.name}
                                onChange={(e) => updateHouseField("name", e.target.value)}
                                className="house-name-input"
                            />
                        </div>

                        {/* NT */}
                        <div style={{display: "flex", flexDirection: "column", alignItems: "center"}}>
                            <span style={{
                                fontSize: "12px",
                                fontWeight: "500",
                                lineHeight: "1",
                                marginBottom: "2px"
                            }}>NT</span>
                            <Select
                                value={selectedHouse.supplement}
                                onChange={(value) => updateHouseField("supplement", value)}
                                className="house-number-input"
                                style={{width: "60px", textAlign: "center"}}
                            >
                                {supplements.map((s) => (
                                    <Select.Option key={s} value={s}>
                                        {s}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <span style={{
                                fontSize: "12px",
                                fontWeight: "500",
                                lineHeight: "1",
                                marginBottom: "2px"
                            }}>Wohnungen</span>
                            <Input
                                type="number"
                                min={0}
                                value={selectedHouse.apartments || "0"}
                                onChange={(e) => {
                                    const value = parseInt(e.target.value, 10);
                                    updateHouseField("apartments", isNaN(value) || value < 0 ? 0 : value);
                                }}
                                className="house-apartments-input"
                                style={{ width: "50px", textAlign: "center" }}
                            />
                        </div>
                        {/* Typ (neues Feld) */}
                        <div style={{display: "flex", flexDirection: "column", alignItems: "center"}}>
                            <span style={{
                                fontSize: "12px",
                                fontWeight: "500",
                                lineHeight: "1",
                                marginBottom: "2px"
                            }}>Typ</span>
                            <Select
                                value={selectedHouse.type}
                                onChange={(value) => updateHouseField("type", value)}
                                className="house-type-select"
                                style={{width: "140px", textAlign: "center"}}
                            >
                                {["Wohngebäude", "Umgebung", "Industriegebäude", "Gewerbegebäude"].map((type) => (
                                    <Select.Option key={type} value={type}>
                                        {type}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                    </div>
                </div>
                <div className="date-fields">
                    {["Bezug", "Geruest", "VormontageAussen", "VormontageInnen"].map((dateType) => (
                        <div key={dateType} className="date-item">
                            <label>{dateLabelMap[dateType]}</label>
                            <DatePicker
                                locale={locale}
                                className="date-picker"
                                size="small"
                                format={"DD.MM.YYYY"}
                                value={selectedHouse.dates[dateType as keyof typeof selectedHouse.dates]
                                    ? dayjs(selectedHouse.dates[dateType as keyof typeof selectedHouse.dates], "DD.MM.YYYY")
                                    : null}
                                onChange={(_, dateString) =>
                                    updateHouseDate(
                                        dateType as keyof typeof selectedHouse.dates,
                                        Array.isArray(dateString) ? dateString[0] : dateString || null
                                    )
                                }
                            />
                        </div>
                    ))}
                </div>
            </div>
            <div className="sections-container">
                {Object.keys(selectedHouse.sections).map((section) => (
                    <div key={section} className="section">
                        <div className="section-header">
                            <span className="section-title">{section}</span>
                            <div className="date-container">
                                <div style={{display: "flex", flexDirection: "column", alignItems: "center"}}>
                                    <span style={{
                                        fontSize: "12px",
                                        lineHeight: "1",
                                        fontWeight: "500",
                                        marginBottom: "2px"
                                    }}>Messbereit ab</span>
                                    <DatePicker
                                        locale={locale}
                                        size="small"
                                        format={"DD.MM.YYYY"}
                                        value={
                                            selectedHouse.sectionDates?.[section]
                                                ? dayjs(selectedHouse.sectionDates[section], "DD.MM.YYYY")
                                                : null
                                        }
                                        onChange={(_, dateString) => {
                                            const value = Array.isArray(dateString) ? dateString[0] : dateString || null;
                                            updateHouseField("sectionDates", {
                                                ...(selectedHouse.sectionDates || {}),
                                                [section]: value
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                            <Button
                                type="primary"
                                icon={<PlusOutlined/>}
                                size="small"
                                onClick={() => addSectionComponent(section as keyof typeof selectedHouse.sections)}
                            />
                        </div>

                        <div className="section-content">
                            {(selectedHouse.sections[section as keyof typeof selectedHouse.sections] as {
                                name: string
                            }[]).map((item, index) => (
                                <div key={index} className="component-item">
                                    <Input
                                        value={item.name}
                                        className="component-input"
                                        onChange={(e) =>
                                                updateSectionComponent(
                                                    section as keyof typeof selectedHouse.sections,
                                                    index,
                                                    e.target.value
                                                )
                                            }
                                        />
                                        <Space>
                                            <Button
                                                icon={<DeleteOutlined/>}
                                                danger
                                                size="small"
                                                onClick={() =>
                                                    deleteSectionComponent(section as keyof typeof selectedHouse.sections, index)
                                                }
                                            />
                                            <Button
                                                icon={<CopyOutlined/>}
                                                size="small"
                                                onClick={() =>
                                                    copySectionComponent(section as keyof typeof selectedHouse.sections, index)
                                                }
                                            />
                                            <Button
                                                icon={<CopyOutlined/>}
                                                size="small"
                                                onClick={() => openCopyManyModal(section, item.name)}
                                            />
                                        </Space>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                <Modal
                    title="Kopien erstellen"
                    open={modalVisible}
                    onOk={handleCopyMany}
                    onCancel={() => setModalVisible(false)}
                >
                    <p>Anzahl</p>
                    <Input
                        type="number"
                        min={1}
                        value={copyAmount}
                        onChange={(e) => setCopyAmount(Number(e.target.value))}
                    />
                    <Checkbox
                        checked={increment}
                        onChange={(e) => setIncrement(e.target.checked)}
                        style={{marginTop: 10}}
                    >
                        Inkrementieren
                    </Checkbox>
                </Modal>
            </div>
            );
            };

            export default OGLAssignment;
